import { useEffect, useRef } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

const siteDomain = typeof __READDY_AI_DOMAIN__ !== 'undefined' && __READDY_AI_DOMAIN__ ? __READDY_AI_DOMAIN__ : 'mulebuyspreadsheets.living';
const siteUrl = siteDomain.startsWith('http') ? siteDomain : `https://${siteDomain}`;

export default function SEO({ title, description, keywords, canonicalPath, ogType = 'website', jsonLd }: SEOProps) {
  const jsonLdRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const canonical = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;
    let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      document.head.appendChild(linkEl);
    }
    linkEl.setAttribute('href', canonical);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonical, true);
    setMeta('og:type', ogType, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    if (jsonLd) {
      if (jsonLdRef.current) {
        jsonLdRef.current.remove();
      }
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      const finalLd = {
        ...jsonLd,
        '@context': 'https://schema.org',
      };
      if (finalLd.url && typeof finalLd.url === 'string' && !finalLd.url.startsWith('http')) {
        finalLd.url = `${siteUrl}${finalLd.url}`;
      }
      if (finalLd.mainEntityOfPage && typeof finalLd.mainEntityOfPage === 'string' && !finalLd.mainEntityOfPage.startsWith('http')) {
        finalLd.mainEntityOfPage = `${siteUrl}${finalLd.mainEntityOfPage}`;
      }
      script.textContent = JSON.stringify(finalLd);
      document.head.appendChild(script);
      jsonLdRef.current = script;
    }

    return () => {
      if (jsonLdRef.current) {
        jsonLdRef.current.remove();
        jsonLdRef.current = null;
      }
    };
  }, [title, description, keywords, canonicalPath, ogType, jsonLd]);

  return null;
}